import type { ReactElement } from "react";
import { useCallback } from "react";

import { useSelector } from "react-redux";

import { TimeoutErrorGate } from "app/pages/sales-intelligence/common-components/error-state/components/TimeoutErrorGate";
import type { TabContentComponentProps } from "app/pages/sales-intelligence/common-components/tabbed-page-content";
import type { PageTabBase } from "app/pages/sales-intelligence/common-components/tabbed-page-content/types";
import {
    SearchResultsTableContainer,
    type LeadsSearchRow,
    type LeadsSearchRowWithKey,
    type LeadsType,
    selectHighlightedRowDomains,
} from "app/pages/sales-intelligence/modules/leads-search";
import ExpandedRowContainer from "app/pages/sales-intelligence/modules/leads-search/containers/search-results-table/ExpandedRow";
import { useLeadsTableResults } from "app/pages/sales-intelligence/modules/leads-search/hooks/useLeadsTableResults";
import { useLeadsSearchTransformData } from "app/pages/sales-intelligence/modules/leads-search/query/select";
import { getLeadDomainFromRow } from "app/pages/sales-intelligence/modules/leads-search/utils/rows";
import { useListsColumnsConfig } from "app/pages/sales-intelligence/pages/opportunity-list/hooks/useListsColumnsConfig";

import useLeadsSearchResult from "../../query/queries/useLeadsSearchResult";

type Props = Pick<TabContentComponentProps<PageTabBase<LeadsType>>, "tab">;

const LeadsSearchResultsTableContainer = ({ tab }: Props): ReactElement => {
    const columnsConfig = useListsColumnsConfig(tab.id);
    const transformData = useLeadsSearchTransformData(columnsConfig, tab.id);

    const { data, isFetching, isPending, isSuccess, isError, error } = useLeadsSearchResult(tab.id, transformData);

    const tableData = useLeadsTableResults({
        data,
        isDataFetchSuccess: isSuccess,
    });

    const domainsToHighlight = useSelector(selectHighlightedRowDomains);

    const rowSelector = (row: LeadsSearchRow) => {
        const domain = getLeadDomainFromRow(row);
        return domainsToHighlight.includes(domain);
    };

    const renderExpandedRow = useCallback(
        (record: LeadsSearchRowWithKey) => (
            <ExpandedRowContainer
                record={record}
                columnsConfig={columnsConfig}
            />
        ),
        [columnsConfig],
    );

    return (
        <TimeoutErrorGate
            isError={isError}
            error={error}
        >
            <SearchResultsTableContainer
                isLoading={isFetching || isPending}
                data={tableData}
                highlightedRowSelector={rowSelector}
                activeRowSelector={rowSelector}
                renderExpandedRow={renderExpandedRow}
            />
        </TimeoutErrorGate>
    );
};

export default LeadsSearchResultsTableContainer;
